import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, model, ChangeDetectionStrategy } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AiAssistant, AiAssistantService, DEFAULT_AVATAR_SVG_ICON } from '../../core';
import { NAMESPACE_CONFIG_TOKEN, SHOW_CUSTOM_ASSISTANT_TOKEN } from '../../core/tokens';
import { combineLatest, firstValueFrom, map } from 'rxjs';
import { AssistantType } from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { TranslateModule } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { aiAssistantAssetUrl } from '../../core/pipes/ai-assistant-asset-url.pipe';
import { DOCUMENT } from '@angular/common';

export interface DialogData {
  activeAssistantId: string;
}

@Component({
  selector: 'ai-assistant-select-dialog',
  imports: [
    CommonModule,
    MatButtonModule,
    GalaxyAvatarModule,
    TranslateModule,
    MatIconModule,
    MatDialogModule,
    GalaxyAlertModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
  ],
  templateUrl: './ai-assistant-select-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./ai-assistant-select-dialog.component.scss'],
})
export class AIAssistantSelectDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AIAssistantSelectDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private assistantService = inject(AiAssistantService);
  private readonly router = inject(Router);
  protected readonly aiIconService = inject(GalaxyAiIconService);

  protected readonly defaultAvatarIcon = DEFAULT_AVATAR_SVG_ICON;
  private readonly document = inject(DOCUMENT);
  private readonly namespaceConfig = toSignal(inject(NAMESPACE_CONFIG_TOKEN));
  private readonly defaultWorkforce = toSignal(this.assistantService.defaultWorkforce$);
  private readonly showCustomAssistant$ = inject(SHOW_CUSTOM_ASSISTANT_TOKEN);

  readonly assistantResource = rxResource({
    params: () => {
      if (!this.namespaceConfig()?.namespace) return;
      return {
        namespace: this.namespaceConfig()?.namespace,
        defaultWorkforce: this.defaultWorkforce() || [],
      };
    },
    stream: ({ params }) =>
      combineLatest([
        this.assistantService.listAllAssistants(params.namespace!, undefined, { surfaceErrors: true }),
        this.showCustomAssistant$,
      ]).pipe(
        map(([res, showCustomAssistant]) => {
          const filtered = res
            .filter((a) => {
              if (a.type === AssistantType.ASSISTANT_TYPE_INBOX) {
                return true;
              }
              if (a.type === AssistantType.ASSISTANT_TYPE_CUSTOM) {
                return showCustomAssistant;
              }
              return false;
            })
            .map(
              (assistant) =>
                this.assistantService.hydrateAssistantWithDefaultInfo(assistant, params.defaultWorkforce) ||
                ({} as AiAssistant),
            );
          return filtered;
        }),
      ),
  });

  readonly selectedAssistant = model<AiAssistant | null>(null);

  protected readonly listingFailed = computed(() => this.assistantResource.status() === 'error');
  // value() throws once the resource holds an error, so every read of the list goes through here.
  protected readonly assistants = computed<AiAssistant[]>(() =>
    this.assistantResource.hasValue() ? this.assistantResource.value() : [],
  );
  // The listing excludes hidden types and the filter above drops custom employees, so a caller
  // with a valid namespace can still have nothing to choose from. Say why rather than show blank.
  // Only a resolved listing supports that claim: idle (no namespace yet), loading and failed are
  // each a different answer, and hasValue() is false for all three.
  protected readonly hasNoOfferableAssistants = computed(
    () => this.assistantResource.hasValue() && this.assistants().length === 0,
  );

  constructor() {
    effect(() => {
      const activeId = this.data.activeAssistantId;
      this.selectedAssistant.set(this.assistants().find((a) => a.assistant.id === activeId) || null);
    });
  }

  protected async navigateToAIWorkforce(): Promise<void> {
    const url = await firstValueFrom(this.assistantService.buildAssistantUrl());
    // Close first: the dialog sits over the page the navigation lands on.
    this.dialogRef.close();
    this.router.navigate([url]);
  }

  selectAssistant(assistant: AiAssistant) {
    this.selectedAssistant.set(assistant || null);
  }

  protected getAvatarSrc(assistant: AiAssistant): string | null {
    return (
      assistant.assistant.avatarUrl ||
      assistant.decoration?.avatarIconUrl ||
      aiAssistantAssetUrl(assistant.decoration?.defaultAvatarIcon, this.document)
    );
  }
}
